/**
 * Hebrew.
 *
 * Carries the copy the app originally shipped with, so nothing was lost in the
 * extraction. Hebrew has three plural categories (one / two / other), which is
 * why `thread.members` cannot be a ternary in code.
 */
import type { MessageKey } from './en.ts';

export const he: Partial<Record<MessageKey, string>> = {
  'app.opening': 'פותח את הכספת…',
  'app.empty.title': 'בחר שיחה',
  'app.empty.body':
    'ההודעות שלך מוצפנות מקצה לקצה. השרת מעביר ביניכם מעטפות סגורות — הוא לא יכול לפתוח אותן, וגם לא מי שישיג גישה אליו.',
  'app.identityAlert.body':
    'מפתח הזהות של {name} השתנה. ייתכן שהתקינו מחדש את האפליקציה — או שמישהו מנסה להתחזות.',
  'app.identityAlert.accept': 'קבל את המפתח החדש',
  'app.identityAlert.dismiss': 'התעלם',

  'onboarding.invited': '{name} הזמין אותך.',
  'onboarding.invited.detail': 'אחרי ההרשמה השיחה איתו תחכה לך.',
  'onboarding.intro': 'הודעות מוצפנות מקצה לקצה. המפתחות נוצרים כאן, במכשיר שלך, ולא עוזבים אותו.',
  'onboarding.username.label': 'שם משתמש',
  'onboarding.username.hint': '3–32 תווים, במערכת כתב אחת.',
  'onboarding.displayName.label': 'שם לתצוגה',
  'onboarding.phone.label': 'מספר טלפון — לא חובה',
  'onboarding.phone.hint':
    'רק כדי שאנשים שכבר יש להם את המספר שלך ימצאו אותך. זה לא שם החשבון שלך, לא כך נכנסים אליו, והסרה שלו בהמשך לא לוקחת איתה שום דבר אחר.',
  'onboarding.submit': 'יצירת חשבון',
  'onboarding.submit.busy': 'יוצר מפתחות…',
  'onboarding.warning':
    'אין סיסמה ואין שחזור. הזהות שלך היא מפתח פרטי ששמור בדפדפן הזה — אם תנקה את נתוני האתר, תצטרך להירשם מחדש.',
  'onboarding.username.error.too_short': 'לפחות 3 תווים.',
  'onboarding.username.error.too_long': 'עד 32 תווים.',
  'onboarding.username.error.bad_start': 'אי אפשר להתחיל בנקודה, מקף או קו תחתון.',
  'onboarding.username.error.disallowed_character': 'יש כאן תו שאינו מותר.',
  'onboarding.username.error.mixed_script':
    'מערבב שתי מערכות כתב. בחר אחת, כדי שלא יוכלו לחקות את השם שלך.',
  'onboarding.username.error.reserved': 'השם הזה שמור.',

  'status.online': 'מחובר',
  'status.connecting': 'מתחבר…',
  'status.offline': 'לא מחובר',
  'sidebar.invite': 'הזמנת חברים',
  'sidebar.findByPhone': 'חיפוש לפי טלפון',
  'sidebar.newChat': 'שיחה חדשה',
  'sidebar.search': 'חיפוש בשיחות',
  'sidebar.conversations': 'שיחות',
  'sidebar.empty': 'אין עדיין שיחות.',
  'sidebar.noMatches': 'לא נמצאו התאמות',
  'sidebar.noMessages': 'אין הודעות עדיין',
  'sidebar.group': 'קבוצה',
  'sidebar.notifications.on': 'התראות פעילות',
  'sidebar.notifications.denied': 'התראות חסומות בדפדפן',
  'sidebar.notifications.enable': 'הפעלת התראות',

  'thread.back': 'חזרה לרשימת השיחות',
  'thread.typing': '{count, plural, one {{names} מקליד…} other {{names} מקלידים…}}',
  'thread.members': '{count, plural, one {משתתף אחד} two {שני משתתפים} other {# משתתפים}}',
  'thread.verifySafety': 'אימות מספר בטיחות',
  'thread.encrypted': 'ההודעות בשיחה זו מוצפנות מקצה לקצה. גם השרת לא יכול לקרוא אותן.',
  'thread.replyingTo': 'תגובה ל: {preview}',
  'thread.attachment': 'קובץ מצורף',
  'thread.cancelReply': 'ביטול תגובה',
  'thread.recording': 'מקליט… שחרר לשליחה',
  'thread.cancelRecording': 'ביטול הקלטה',
  'thread.composer': 'הודעה',
  'thread.send': 'שליחה',
  'thread.voice.unavailable.title': 'הקלטה קולית דורשת HTTPS — בכתובת הזו הדפדפן חוסם את המיקרופון',
  'thread.voice.unavailable.label': 'הקלטה קולית לא זמינה ללא HTTPS',
  'thread.voice.start': 'הקלטת הודעה קולית',
  'thread.voice.stop': 'סיום ושליחה',

  'bubble.deleted': 'ההודעה נמחקה',
  'bubble.actions': 'פעולות על ההודעה',
  'bubble.reply': 'תגובה',
  'profile.profiles': 'פרופילים',
  'profile.profiles.explain': 'חשבונות נפרדים על המכשיר הזה — עבודה, פרטי, עסק. כולם נשארים מחוברים, כך שהודעה לכל אחד מהם מתריעה לך.',
  'profile.profiles.add': 'הוספת פרופיל',
  'profile.profiles.create': 'יצירה',
  'profile.profiles.namePlaceholder': 'עבודה, פרטי…',
  'profile.profiles.unnamed': 'פרופיל ללא שם',
  'profile.profiles.remove': 'מחיקת פרופיל',
  'profile.profiles.removeConfirm': 'למחוק את החשבון הזה לתמיד?',
  'profile.profiles.warning': 'פרופיל חדש הוא חשבון חדש עם שם משתמש משלו — לא עוד תצוגה של הקיים. גם אותו אי אפשר לשחזר.',
  'profile.build': 'גרסה {build}',
  'profile.name.edit': 'שינוי שם',
  'profile.name.save': 'שמירה',
  'profile.name.cancel': 'ביטול',
  'profile.name.failed': 'לא הצלחנו לשנות את השם. בדוק את החיבור ונסה שוב.',
  'profile.delete': 'מחיקת חשבון',
  'profile.delete.explain': 'הפעולה מוחקת את החשבון מהשרת לתמיד. המפתחות שלך קיימים רק כאן, אז אין דרך חזרה ואין מה לשחזר.',
  'profile.delete.others': 'הודעות ששלחת כבר נשארות במכשירים שקיבלו אותן. לאף אחד לא נמסר שעזבת — הם יגלו אם ינסו לכתוב לך.',
  'profile.delete.confirmPrompt': 'הקלד את שם המשתמש שלך לאישור',
  'profile.delete.confirm': 'למחוק לתמיד',
  'profile.delete.failed': 'לא הצלחנו למחוק את החשבון. בדוק את החיבור ונסה שוב.',
  'thread.recipientGone': 'האדם הזה כבר לא ב-VAV. ההודעות שלך לא יגיעו אליו.',
  'thread.recipientGone.title': 'החשבון הזה נמחק',
  'profile.avatar.change': 'שינוי תמונה',
  'profile.avatar.remove': 'הסרת התמונה',
  'profile.avatar.explain': 'התמונה שלך מוצפנת ונשלחת רק לאנשים שאתה מדבר איתם. זרים רואים את ראשי התיבות שלך.',
  'avatar.crop.title': 'התאמת התמונה',
  'avatar.crop.hint': 'גרור למיקום, הזז את המחוון לזום. מה שבתוך העיגול הוא מה שכולם יראו.',
  'avatar.crop.zoom': 'זום',
  'avatar.crop.loadFailed': 'לא ניתן לקרוא את התמונה הזאת. ייתכן שזה פורמט שהאפליקציה לא תומכת בו — נסה תמונה אחרת.',
  'call.incoming.audio': 'שיחה קולית נכנסת',
  'call.incoming.video': 'שיחת וידאו נכנסת',
  'call.accept': 'מענה',
  'call.decline': 'דחייה',
  'call.outgoing': 'מצלצל…',
  'call.cancel': 'ביטול',
  'call.hangup': 'סיום שיחה',
  'call.mute': 'השתקה',
  'call.unmute': 'ביטול השתקה',
  'call.cameraOff': 'כיבוי מצלמה',
  'call.cameraOn': 'הפעלת מצלמה',
  'call.connecting': 'מתחבר…',
  'call.ended.declined': 'השיחה נדחתה',
  'call.ended.busy': 'עסוק',
  'call.ended.hangup': 'השיחה הסתיימה',
  'call.ended.failed': 'לא ניתן להתחיל את השיחה',
  'call.ended.unanswered': 'אין מענה',
  'thread.call.audio': 'שיחה קולית',
  'thread.call.video': 'שיחת וידאו',
  'call.legFailed': 'החיבור לא נוצר',
  'call.turnMissing': 'שרת המעבר (TURN) עדיין לא מוגדר — שיחות בין רשתות שונות זקוקות לו',
  'call.forProfile': 'אל הפרופיל שלך {name}',
  'call.toSpeaker': 'רמקול',
  'call.toEarpiece': 'אפרכסת',
  'onboarding.link.start': 'יש לי כבר חשבון — קשר את המכשיר הזה',
  'onboarding.link.title': 'קישור המכשיר הזה',
  'onboarding.link.hint': 'במכשיר שכבר יש בו את החשבון שלך: פרטים שלי, קישור מכשיר חדש, וסרוק את הקוד או הדבק אותו.',
  'onboarding.link.verifyHint': 'ודא שהספרות האלה מופיעות גם במכשיר המאשר.',
  'onboarding.link.copy': 'העתקת קוד קישור',
  'onboarding.link.copied': 'הועתק ✓',
  'onboarding.link.waiting': 'ממתין לאישור…',
  'onboarding.link.failed': 'הקישור לא הושלם. התחל מחדש בשני המכשירים.',
  'profile.device.remove': 'ניתוק',
  'profile.logout': 'התנתקות מהמכשיר הזה',
  'profile.logout.explain': 'החשבון נשאר במכשירים האחרים שלך. כל מה ששמור במכשיר הזה יימחק ממנו.',
  'profile.logout.confirm': 'התנתקות',
  'link.approve.stranded': 'בדפדפן הזה אין את החשבון שלך, ולכן אי אפשר לאשר כאן את המכשיר החדש. פתח את הקישור (או הדבק את הקוד) באפליקציית VAV במכשיר שבו נמצא החשבון.',
  'link.approve.title': 'אישור מכשיר חדש',
  'link.approve.account': 'המכשיר החדש יצורף לחשבון {name}.',
  'profile.link.start': 'קישור מכשיר חדש',
  'profile.link.hint': 'הדבק את קוד הקישור שמוצג במכשיר החדש.',
  'profile.link.verifyHint': 'אשר רק אם אותן ספרות מוצגות במכשיר החדש.',
  'profile.link.approve': 'אישור המכשיר',
  'profile.link.done': 'המכשיר קושר',
  'profile.link.failed': 'האישור נכשל — ייתכן שהקוד פג.',
  'thread.date.today': 'היום',
  'thread.date.yesterday': 'אתמול',
  'contact.delete': 'מחיקת איש קשר',
  'contact.delete.explain': 'השיחה וההיסטוריה יימחקו מהמכשיר הזה בלבד. הוא לא יקבל הודעה, והעותק שלו נשאר אצלו.',
  'group.leave': 'יציאה מהקבוצה',
  'group.leave.explain': 'החברים יראו שעזבת. הקבוצה וההיסטוריה שלה יימחקו מהמכשיר הזה.',
  'contact.title': 'פרטי איש הקשר',
  'contact.verified': 'מאומת',
  'profile.title': 'הפרטים שלי',
  'profile.username': 'שם משתמש',
  'profile.thisDevice': 'המכשיר הזה',
  'profile.devices': 'מכשירים',
  'profile.devicesLoading': 'טוען…',
  'profile.devicesNone': 'לא הצלחנו להגיע לשרת.',
  'profile.deviceCurrent': 'זה שאתה בו',
  'profile.install': 'התקנה על המכשיר הזה',
  'profile.install.action': 'הוספה למסך הבית',
  'profile.install.already': 'אתה משתמש באפליקציה המותקנת.',
  'profile.install.ios': 'פתח את תפריט השיתוף ובחר "הוסף למסך הבית".',
  'profile.install.unavailable': 'הדפדפן לא מציע התקנה כרגע — בדרך כלל כי האפליקציה כבר מותקנת. חפש את VAV ברשימת האפליקציות.',
  'profile.noRecovery': 'המפתחות שלך קיימים רק על המכשיר הזה. ניקוי נתוני הדפדפן או אובדן הטלפון מאבד את החשבון — אין שחזור.',
  'bubble.forward': 'העברה',
  'bubble.select': 'בחירה',
  'bubble.moreReactions': 'עוד תגובות',
  'select.cancel': 'ביטול הבחירה',
  'select.count': '{count, plural, =0 {בחירת הודעות} one {נבחרה הודעה אחת} two {נבחרו # הודעות} other {נבחרו # הודעות}}',
  'forward.title': 'העברה אל',
  'forward.send': 'שליחת {count, plural, one {הודעה אחת} two {# הודעות} other {# הודעות}} אל {to, plural, one {שיחה אחת} two {# שיחות} other {# שיחות}}',
  'thread.voice.blocked.how': 'פתח את vav.chat בדפדפן Chrome (לא באפליקציה המותקנת), לחץ על הסמל משמאל לשורת הכתובת ← הרשאות ← איפוס הרשאות. אחר כך לחץ שוב על המיקרופון ואשר.',
  'thread.voice.retry': 'נסה שוב',
  'thread.voice.blockedByOs': 'לאתר הזה יש הרשאה למיקרופון, כלומר החסימה היא לא כאן — לדפדפן עצמו אין הרשאת מיקרופון מהטלפון.',
  'thread.voice.blockedByOs.how': 'אנדרואיד: הגדרות ← אפליקציות ← Chrome (או VAV, אם אתה באפליקציה המותקנת) ← הרשאות ← מיקרופון ← אפשר.',
  'thread.voice.sendFailed': 'לא הצלחנו לשלוח את ההקלטה. בדוק את החיבור ונסה שוב.',
  'thread.voice.busy': 'המיקרופון מורשה אבל לא ניתן לפתוח אותו. כנראה אפליקציה אחרת משתמשת בו.',
  'thread.voice.busy.how': 'סגור כל דבר שעשוי להחזיק אותו — שיחה, עוזר קולי, מקליט — ונסה שוב. הפעלה מחדש של הטלפון משחררת מיקרופון תקוע.',
  'thread.unreadFrom': '{count, plural, one {הודעה חדשה אחת} two {# הודעות חדשות} other {# הודעות חדשות}}',
  'thread.voice.blocked': 'המיקרופון חסום לאתר הזה. אחרי סירוב הדפדפן לא ישאל שוב — צריך לשנות את זה בהגדרות.',
  'thread.voice.noMicrophone': 'לא נמצא מיקרופון במכשיר הזה.',
  'push.prompt.title': 'ההתראות כבויות',
  'push.prompt.body': 'הפעל אותן והטלפון יודיע לך כשמגיעה הודעה, גם כשהאפליקציה סגורה.',
  'push.prompt.denied': 'האתר חסום משליחת התראות. אפשר אותן בהגדרות האתר בדפדפן, ואז חזור לכאן.',
  'push.prompt.enable': 'הפעלה',
  'push.prompt.dismiss': 'לא עכשיו',
  'quote.you': 'אתה',
  'quote.them': 'הם',
  'quote.unavailable': 'ההודעה אינה זמינה',
  'quote.image': 'תמונה',
  'quote.video': 'סרטון',
  'quote.audio': 'אודיו',
  'quote.voice': 'הודעה קולית',
  'schedule.title': 'שליחה מאוחר יותר',
  'schedule.explain': 'ההודעה נכתבת ונחתמת עכשיו, ויוצאת בזמן שתבחר. עד אז אפשר לבטל אותה מתפריט ה-⋯ שלה.',
  'schedule.confirm': 'תזמון',
  'bubble.cancelScheduled': 'ביטול השליחה המתוזמנת',
  'bubble.state.scheduled': 'מתוזמן',
  'profile.checkUpdate': 'בדיקת עדכון',
  'profile.checkUpdate.busy': 'בודק…',
  'profile.checkUpdate.current': 'אתה על הגרסה העדכנית.',
  'bubble.edit': 'עריכה',
  'bubble.edited': 'נערכה',
  'thread.editing': 'עריכת הודעה',
  'group.edit.title': 'פרטי הקבוצה',
  'group.edit.name': 'שם הקבוצה',
  'bubble.deleteForMe': 'מחיקה אצלי',
  'bubble.save': 'שמירת התמונה',
  'bubble.closeImage': 'סגירת התמונה',
  'bubble.deleteForEveryone': 'מחיקה לכולם',
  'bubble.decryptFailed': 'לא ניתן לפענח את הקובץ המצורף.',
  'bubble.decrypting': 'מפענח…',
  'bubble.state.pending': 'ממתין',
  'bubble.state.sent': 'נשלח',
  'bubble.state.delivered': 'נמסר',
  'bubble.state.read': 'נקרא',
  'bubble.state.failed': 'נכשל',

  'attach.label': 'צירוף',
  'attach.camera': 'מצלמה',
  'attach.image': 'תמונה',
  'attach.video': 'וידאו',
  'attach.audio': 'אודיו',
  'attach.document': 'מסמך',

  'media.play': 'ניגון',
  'media.pause': 'השהיה',
  'media.seek': 'מיקום בהקלטה',
  'media.decrypting': 'מפענח…',

  'invite.title': 'הזמנת חברים',
  'invite.close': 'סגירה',
  'invite.body':
    'שלח את הקישור. מי שיפתח אותו יראה שהזמנת אותו, ואחרי ההרשמה השיחה איתך כבר תחכה לו.',
  'invite.preparing': 'מכין קישור…',
  'invite.share': 'שיתוף',
  'invite.copy': 'העתקת קישור',
  'invite.copied': 'הועתק',
  'invite.copyFailed': 'לא ניתן להעתיק — סמן את הקישור והעתק ידנית',
  'invite.caution':
    'הקישור תקף 30 יום וניתן לשימוש חוזר. כל אחד שמחזיק בו יכול להירשם ולראות שאתה המזמין — אל תפרסם אותו במקום ציבורי.',
  'invite.message': '{name} מזמין אותך ל-VAV — הודעות מוצפנות מקצה לקצה.',
  'invite.defaultName': 'חבר',

  'newChat.title': 'שיחה חדשה',
  'newChat.search': 'שם משתמש, שם מלא או מספר טלפון',
  'newChat.direct': 'שיחה',
  'newChat.group': 'קבוצה',
  'newChat.groupName': 'שם הקבוצה',
  'newChat.pickHint': 'בחר משתתפים מהתוצאות — אפשר כמה שתרצה.',
  'newChat.searching': 'מחפש…',
  'newChat.noResults': 'לא נמצאו משתמשים בשם הזה.',
  'newChat.createGroup': 'יצירת קבוצה',
  'newChat.openChat': 'פתיחת שיחה',

  'phone.title': 'מספר טלפון',
  'phone.privacy': 'המספר מוצפן במכשיר שלך לפני שהוא נשלח. השרת שומר רק את הטביעה, לא את המספר.',
  'phone.mine': 'המספר שלי — כדי שימצאו אותי',
  'phone.save': 'שמור',
  'phone.saved': 'נשמר',
  'phone.findFriends': 'חיפוש חברים',
  'phone.fromContacts': 'בחר מאנשי הקשר',
  'phone.noContactPicker':
    'הדפדפן הזה לא נותן גישה לאנשי הקשר. הקלד מספר לבדיקה, או השתמש ב-Chrome באנדרואיד.',
  'phone.checkOne': 'בדיקת מספר בודד',
  'phone.search': 'חפש',
  'phone.noneRegistered': 'אף אחד מהמספרים האלה לא רשום.',
  'phone.invalid': 'זה לא נראה כמו מספר טלפון',

  'safety.title': 'מספר בטיחות',
  'safety.compare':
    'השוו את המספר הזה מול {name} בערוץ אחר — פגישה, שיחת טלפון. אם המספרים זהים, אף אחד לא יושב באמצע.',
  'safety.confirm': 'אימתתי את המספר הזה',
  'safety.changeNote':
    'אם {name} יתקין מחדש את האפליקציה, המספר ישתנה ותקבלו התראה. שינוי בלתי צפוי שווה בדיקה לפני שממשיכים לשלוח.',
  'safety.noSession': 'עדיין לא הוחלפו מפתחות עם {name}. שלחו הודעה אחת וחזרו לכאן.',
  'common.cancel': 'ביטול',

  'push.channel': 'הודעות',
  'push.newMessage': 'הודעה חדשה',
  'app.identityAlert.title': 'מפתח הזהות השתנה',
  'media.permission.library': 'אין הרשאה לגלריה',
  'media.permission.camera': 'אין הרשאה למצלמה',
  'media.permission.microphone': 'אין הרשאה למיקרופון',
  'attach.error.title': 'לא ניתן לצרף',
  'thread.voice.error.title': 'לא ניתן להקליט',
  'newChat.searchByUsername': 'חיפוש לפי שם משתמש',
  'language.title': 'שפה',
  'language.note': 'מזוהה מהדפדפן שלך. שנה כאן אם הזיהוי שגוי.',
  'language.restart': 'הפעל מחדש את האפליקציה כדי להשלים את שינוי כיוון הפריסה.',
  'app.failed.title': 'לא הצליח לעלות',
  'app.failed.retry': 'נסה שוב',
  'phone.invalidHint': 'נסה את הצורה הבינלאומית המלאה, שמתחילה בקידומת המדינה.',
  'phone.country': 'מדינה',
  'phone.national': 'המספר שלך, כפי שהיית מחייג מקומית',
  'phone.willSave': 'יישמר בתור {number}',
  'phone.searchCountry': 'חיפוש מדינה',
  'phone.importContacts': 'חיפוש באנשי הקשר שלי',
  'phone.importing': 'בודק את אנשי הקשר שלך…',
  'phone.importingProgress': 'בודק את אנשי הקשר שלך… {done} מתוך {total}',
  'phone.importScanning': 'קורא את אנשי הקשר שלך…',
  'phone.importScanned': '{entries} רשומות, {unique, plural, one {מספר # לבדיקה} two {# מספרים לבדיקה} many {# מספרים לבדיקה} other {# מספרים לבדיקה}}',
  'phone.contactsDenied': 'ההרשאה לקרוא אנשי קשר נדחתה. עדיין אפשר לבדוק מספר ידנית.',
  'phone.matched': '{count, plural, =0 {אף אחד מאנשי הקשר שלך עדיין לא כאן} one {איש קשר אחד כאן} two {שני אנשי קשר כאן} other {# אנשי קשר כאן}}',
  'phone.contactsPrivacy': 'אנשי הקשר שלך עוברים גיבוב במכשיר הזה. פנקס הכתובות עצמו לעולם לא נשלח.',
  'invite.share.subject': 'הצטרפו אליי ל-VAV',
};
